"use client";

import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EVENTS_API } from "@/lib/apis";
import { TEvent, TEventFormData } from "@/types/events";
import { SquarePen, Trash2, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { CommunityEventForm } from "./community-event-form";
import { CardContent } from "@/components/new/card";
import { Button } from "@/components/new/button";
import { EventDetails } from "./community-event-details";

interface EventInfoDialogProps {
	event: TEvent | null;
	isOpen: boolean;
	onClose: () => void;
	onEventDeleted: (slug: string) => void;
	onEventUpdated: (event: TEvent) => void;
}

export const EventInfoDialog = ({
	event,
	isOpen,
	onClose,
	onEventDeleted,
	onEventUpdated,
}: EventInfoDialogProps) => {
	const [isEditing, setIsEditing] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	if (!event) return null;

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			const response = await fetch(`${EVENTS_API}/${event.slug}`, {
				method: "DELETE",
				credentials: "include",
			});

			if (response.ok) {
				toast.success("Event deleted successfully!");
				onClose();
				onEventDeleted(event.slug);
			} else {
				const errorData = await response.json();
				toast.error(errorData.message || "Failed to delete the event.");
			}
		} catch (error) {
			console.error("Client-side error deleting event:", error);
			toast.error("An error occurred while deleting the event.");
		} finally {
			setIsDeleting(false);
		}
	};

	const handleUpdate = async (data: TEventFormData) => {
		try {
			const response = await fetch(`${EVENTS_API}/${event.slug}`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
				},
				credentials: "include",
				body: JSON.stringify(data),
			});

			if (response.ok) {
				const { data: updatedEvent } = await response.json();
				toast.success("Event updated successfully!");
				setIsEditing(false);
				onEventUpdated(updatedEvent);
				onClose();
			} else {
				const errorData = await response.json();
				toast.error(errorData.message || "Failed to update event.");
			}
		} catch (error) {
			console.error("Client-side error updating event:", error);
			toast.error("An error occurred while updating the event.");
		}
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={() => {
				onClose();
				setIsEditing(false);
			}}
		>
			<DialogContent className="max-w-[650px]">
				{isEditing ? (
					<>
						<DialogHeader>
							<DialogTitle>Edit Event</DialogTitle>
						</DialogHeader>
						<CommunityEventForm
							onSubmit={handleUpdate}
							initialData={{
								...event,
								dateTime: event.dateTime ?? new Date().toISOString(),
								endDateTime: event.endDateTime ?? new Date().toISOString(),
							}}
						/>
					</>
				) : (
					<>
						<div className="space-y-6 px-3">
							<div className="absolute right-14 top-[10px] flex items-center gap-4 border-0 outline-none">
								<Button
									onClick={() => setIsEditing(true)}
									className="m-0 p-0 text-blue-600 hover:bg-transparent hover:text-blue-700 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
									title="Edit event"
									variant={"ghost"}
								>
									<SquarePen className="h-5 w-5" />
								</Button>

								<Button
									onClick={handleDelete}
									className="m-0 p-0 text-red-600 hover:bg-transparent hover:text-red-700 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
									title="Delete event"
									variant={"ghost"}
									disabled={isDeleting}
								>
									{isDeleting ? (
										<Loader2 className="h-5 w-5 animate-spin" />
									) : (
										<Trash2 className="h-5 w-5" />
									)}
								</Button>
							</div>
						</div>

						<CardContent className="relative space-y-6 p-3">
							<EventDetails event={event} componentType="vertical" />
						</CardContent>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
